/* Sundays in Bed — Free Interior Design Quotation flow.
   One question per screen; contractor-works screen appears only when an estimate is requested.
   Pricing/state keys are code-controlled; copy comes from section settings with code fallbacks
   (fallbacks matter: route-cached HTML may carry an older config for a few hours).
   PII (name/email/phone) lives in memory only and is cleared after success; other answers persist in sessionStorage.
   No RM figure is ever rendered before the contact form is submitted. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SIBQuiz = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---- validators (pure; unit-tested in node) ---- */
  var validators = {
    email: function (v) { return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()); },
    required: function (v) { return typeof v === 'string' ? v.trim().length > 0 : v != null; },
    nonEmptyArray: function (v) { return Array.isArray(v) && v.length > 0; },
    phone: function (v) {
      if (typeof v !== 'string') return false;
      var d = v.replace(/[\s\-().]/g, '');
      return /^\+?\d{7,15}$/.test(d);
    },
    oneOf: function (v, allowed) { return allowed.indexOf(v) >= 0; },
    subsetOf: function (arr, allowed) {
      return Array.isArray(arr) && arr.every(function (v) { return allowed.indexOf(v) >= 0; });
    }
  };

  /* Stable option keys — code-controlled. Labels are presentation only. */
  var KEYS = {
    siteConditions: ['new_developer', 'resale_older'],
    projectPurposes: ['own_stay', 'rental'],
    homeTypes: ['condo', 'terrace', 'semi-d', 'bungalow', 'other'],
    locations: ['kl', 'selangor', 'other'],
    services: ['styling', 'procurement', 'virtual', 'design', 'not-sure'],
    budgetBands: ['rm10-50k', 'rm50-100k', 'rm100-150k', 'rm150-200k', 'rm200-300k', 'rm300-500k', 'rm500k-plus'],
    contractorPrefs: ['include', 'own', 'none', 'unsure'],
    works: ['plumb', 'floor', 'paint', 'elec', 'kit', 'ward', 'ffe', 'wet'],
    tiers: ['e', 'p', 's']
  };
  /* Rental defaults to these two services (pre-selected, not locked) the moment "Rental" is
     chosen — only when services are still untouched, so it never clobbers a manual edit made
     before revisiting this question. */
  var RENTAL_DEFAULT_SERVICES = ['styling', 'procurement'];
  /* 'not-sure' clears every other service and vice versa */
  var EXCLUSIVE_SERVICE = 'not-sure';

  /* Code-side option labels (fallbacks; cfg lists override only when their keys match KEYS) */
  /* UI home-type key -> BOQ engine's 01_Project property-type string (SITE_FACTORS lookup) */
  var HOME_TYPE_TO_PROPERTY_TYPE = {
    condo: 'Condominium', terrace: 'Terrace', 'semi-d': 'Semi-D', bungalow: 'Bungalow', other: 'Terrace'
  };

  var OPTS = {
    siteConditions: [
      { value: 'new_developer', label: 'New unit' },
      { value: 'resale_older', label: 'Resale / older property' }
    ],
    projectPurposes: [
      { value: 'own_stay', label: 'Own stay' },
      { value: 'rental', label: 'Rental' }
    ],
    homeTypes: [
      { value: 'condo', label: 'Condominium / Apartment' },
      { value: 'terrace', label: 'Terrace' },
      { value: 'semi-d', label: 'Semi-D' },
      { value: 'bungalow', label: 'Bungalow' },
      { value: 'other', label: 'Other' }
    ],
    locations: [
      { value: 'kl', label: 'Kuala Lumpur' },
      { value: 'selangor', label: 'Selangor' },
      { value: 'other', label: 'Others' }
    ],
    services: [
      { value: 'styling', label: 'Interior Styling', desc: 'We restyle each room with what you own.' },
      { value: 'procurement', label: 'Procurement Service', desc: 'We buy everything for you — ordering, deliveries, returns.' },
      { value: 'virtual', label: 'Virtual Design', desc: 'Design over video, from your floor plan — from anywhere.' },
      { value: 'design', label: 'Interior Design', desc: 'Everything from design to move-in. Concierge included.' },
      { value: 'not-sure', label: 'Not sure', desc: 'Help me figure out what my home needs.' }
    ],
    budgetBands: [
      { value: 'rm10-50k', label: 'RM10,000–50,000' },
      { value: 'rm50-100k', label: 'RM50,000–100,000' },
      { value: 'rm100-150k', label: 'RM100,000–150,000' },
      { value: 'rm150-200k', label: 'RM150,000–200,000' },
      { value: 'rm200-300k', label: 'RM200,000–300,000' },
      { value: 'rm300-500k', label: 'RM300,000–500,000' },
      { value: 'rm500k-plus', label: 'More than RM500,000' }
    ],
    contractorPrefs: [
      { value: 'include', label: 'Yes, include an estimate', desc: 'Give me an indicative range for contractor works.' },
      { value: 'own', label: 'I have my own contractor', desc: 'I already have someone handling the building works.' },
      { value: 'none', label: 'I don’t need contractor works', desc: 'Design, furniture or styling only.' },
      { value: 'unsure', label: 'I’m not sure yet', desc: 'We can talk it through on a call.' }
    ],
    tiers: [
      { value: 'e', label: 'Essential', desc: 'Quality local brands. Laminate flooring, melamine carpentry.' },
      { value: 'p', label: 'Premium', desc: 'Local and imported mix. Engineered wood, quartz tops.' },
      { value: 's', label: 'Signature', desc: 'Imported brands. Natural stone, bespoke joinery.' }
    ],
    works: [
      { value: 'plumb', label: 'Plumbing works' },
      { value: 'floor', label: 'Flooring change' },
      { value: 'paint', label: 'Painting & ceiling' },
      { value: 'elec', label: 'Lighting & wiring' },
      { value: 'kit', label: 'Kitchen carpentry' },
      { value: 'ward', label: 'Built-in wardrobe' },
      { value: 'ffe', label: 'Furniture, fixtures & equipment (FF&E)' },
      { value: 'wet', label: 'Wet-area tiling (bathroom & kitchen)' }
    ]
  };
  function optLabel(list, val) {
    var hit = list.filter(function (o) { return o.value === val; })[0];
    return hit ? hit.label : (val || '—');
  }

  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') {
    return { validators: validators, KEYS: KEYS, OPTS: OPTS };
  }

  var STORE_KEY = 'sib-quiz-v2'; /* non-PII answers only (v2: new question model) */

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'sib-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  var SibQuiz = function () { return Reflect.construct(HTMLElement, [], SibQuiz); };
  SibQuiz.prototype = Object.create(HTMLElement.prototype);

  /* All flow-specific styles ship with this asset (not the section stylesheet) so they
     reach storefront paths whose HTML snapshot is still route-cached. */
  var FLOW_CSS = [
    /* split layout: question pinned left, options right (rooms screen) */
    '.sib .sq-screen.sq-split{display:grid;grid-template-columns:minmax(250px,.75fr) minmax(0,1.5fr);gap:10px 60px;align-items:start;}',
    '.sib .sq-split .sq-head{position:sticky;top:130px;text-align:left;}',
    '.sib .sq-split .sq-head .sq-h,.sib .sq-split .sq-head .sq-sub{text-align:left;margin-left:0;margin-right:0;}',
    '.sib .sq-split>.sq-nav{grid-column:1/-1;}',
    '@media(max-width:899px){.sib .sq-screen.sq-split{display:block;}.sib .sq-split .sq-head{position:static;}}',
    /* room circles: line icon + label inside, stepper below */
    '.sib .sq-roomgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(128px,1fr));gap:28px 14px;justify-items:center;margin-top:6px;}',
    '.sib .sq-roomitem{text-align:center;width:100%;}',
    '.sib .sq-roomcircle{position:relative;width:min(132px,92%);aspect-ratio:1;border-radius:50%;margin:0 auto;cursor:pointer;background:#F3EDE2;border:none;padding:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;transition:box-shadow .25s ease,transform .25s ease,background .25s ease;}',
    '.sib .sq-roomcircle svg{width:38px;height:38px;stroke:#33291D;fill:none;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round;}',
    '.sib .sq-roomcircle:hover{transform:translateY(-2px);box-shadow:0 6px 18px rgba(51,41,29,.12);}',
    '.sib .sq-roomcircle[aria-pressed=true]{background:#F2D9AE;}',
    '.sib .sq-roomcheck{position:absolute;top:4px;right:8px;z-index:2;width:24px;height:24px;border-radius:50%;background:#FDFBF6;border:1px solid #E2D8C6;display:flex;align-items:center;justify-content:center;font-size:12px;color:#33291D;}',
    '.sib .sq-roomlabelin{font-size:12.5px;line-height:1.3;color:#33291D;max-width:84%;text-align:center;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-roomstep{display:flex;align-items:center;justify-content:center;gap:12px;margin-top:10px;}',
    '.sib .sq-roomstep button{width:26px;height:26px;border-radius:50%;border:none;background:#F5E3CB;color:#33291D;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;}',
    '.sib .sq-roomstep button:hover{background:#EDD3B1;}',
    '.sib .sq-roomcount{font-size:14px;color:#33291D;min-width:14px;text-align:center;font-family:"Work Sans",sans-serif;}',
    /* question headings */
    '.sib .sq-h{font-size:clamp(21px,2.7vw,30px);}',
    /* built-up-area slider (same UI as the cost calculator) */
    '.sib .sq-sliderwrap{max-width:620px;margin:26px auto 4px;text-align:left;}',
    '.sib .sq-sliderrow{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px;}',
    '.sib .sq-sllabel{font-size:13px;color:#6F6252;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-slout{font-size:16px;font-weight:500;color:#33291D;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-sliderwrap input[type=range]{width:100%;accent-color:#33291D;}',
    /* multi-select service cards reuse .sq-card; pressed state */
    '.sib .sq-card[aria-pressed=true]{border:2px solid #33291D;padding:15px;}',
    /* works screen: finish cards + checkbox rows (same look as the calculator) */
    '.sib .sq-lbl{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#6F6252;margin:26px 0 10px;text-align:left;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-tiergrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;}',
    '.sib .sq-tier{border:1px solid #E2D8C6;background:#FDFAF4;padding:16px;cursor:pointer;text-align:left;color:#33291D;width:100%;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-tier.on{border:2px solid #33291D;padding:15px;}',
    '.sib .sq-tier b{font-size:16px;font-weight:500;display:block;margin-bottom:6px;}',
    '.sib .sq-tier span{font-size:14px;color:#6F6252;line-height:1.6;display:block;}',
    '.sib .sq-scpgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:8px;}',
    '.sib .sq-scp{display:flex;align-items:center;gap:12px;padding:13px 16px;background:#FDFAF4;border:1px solid #E2D8C6;cursor:pointer;font-size:15px;text-align:left;font-family:"Work Sans",sans-serif;color:#33291D;}',
    '.sib .sq-scp input[type=checkbox]{width:16px;height:16px;accent-color:#33291D;margin:0;flex-shrink:0;}',
    '.sib .sq-worksintro{font-size:13.5px;line-height:1.75;color:#6F6252;max-width:620px;margin:0 auto;}',
    /* nav extras */
    '.sib .sq-next:disabled{opacity:.45;cursor:not-allowed;transform:none;}',
    '.sib .sq-total{font-size:13px;color:#6F6252;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-nav.sq-navcenter{justify-content:center;}',
    /* read-only report — mirrors /pages/cost-calculator's answer panel */
    '.sib .sq-report{max-width:860px;margin:0 auto;text-align:left;}',
    '.sib .sq-repsum{background:#FDFAF4;border:1px solid #E2D8C6;padding:26px 24px;}',
    '.sib .sq-repsum h3{font-family:"Newsreader",Georgia,serif;font-weight:400;font-size:22px;margin:0 0 14px;color:#33291D;}',
    '.sib .sq-reprow{display:flex;justify-content:space-between;align-items:baseline;gap:18px;padding:9px 0;border-bottom:1px solid #EFE7D8;}',
    '.sib .sq-reprow:last-child{border-bottom:none;}',
    '.sib .sq-replbl{font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:#6F6252;flex-shrink:0;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repval{font-size:14.5px;color:#33291D;text-align:right;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repans{background:#33291D;padding:36px 24px;margin-top:16px;}',
    '.sib .sq-repans .sq-ansrow{display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;padding:6px 0;}',
    '.sib .sq-repans .sq-anslbl{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:#A6957C;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repans .sq-ansval{font-family:"Newsreader",Georgia,serif;font-size:26px;color:#F8F3EB;}',
    '.sib .sq-repans .sq-anssep{border-bottom:1px solid #4A4234;padding-bottom:16px;margin-bottom:16px;}',
    '.sib .sq-repans .sq-anstotal{font-family:"Newsreader",Georgia,serif;font-size:34px;color:#F8F3EB;}',
    '.sib .sq-repans .sq-ansnote{font-size:12.5px;line-height:1.7;color:#C6B89F;margin:10px 0 24px;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repans .sq-anscta{display:inline-block;padding:16px 30px;font-size:12px;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;font-family:"Work Sans",sans-serif;font-weight:500;border:1px solid #F8F3EB;background:#F8F3EB;color:#33291D;}',
    '.sib .sq-repans .sq-ansdisc{font-size:12.5px;line-height:1.7;color:#7A6F5D;margin:18px 0 0;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repstatus{text-align:center;font-size:14px;color:#5C7150;margin:0 0 18px;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repedit{max-width:860px;margin:16px auto 0;text-align:left;}',
    '.sib .sq-editnote{font-size:12.5px;line-height:1.6;color:#6F6252;font-family:"Work Sans",sans-serif;margin:0;padding:14px 16px;background:#FBF6EE;border:1px solid #E2D8C6;}',
    '@media(prefers-reduced-motion:reduce){.sib .sq-roomcircle{transition:none;}.sib .sq-roomcircle:hover{transform:none;box-shadow:none;}}'
  ].join('');

  /* Line icons per engine room key — drawn for a Malaysian home */
  var svgIcon = function (paths) { return '<svg viewBox="0 0 48 48" aria-hidden="true">' + paths + '</svg>'; };
  var ICONS = {
    bed: svgIcon('<path d="M8 40V16M40 40V30M8 30h28a4 4 0 0 1 4 4M8 36h32M12 30v-6h10v6"/>'),
    liv: svgIcon('<path d="M12 26v-6a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v6M6 29a3 3 0 0 1 6 0v5h24v-5a3 3 0 0 1 6 0v7a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2zM13 38v3M35 38v3"/>'),
    din: svgIcon('<path d="M24 7v5M20 16l4-4 4 4M10 27h28M14 27v12M34 27v12M24 27v6"/>'),
    kit: svgIcon('<rect x="10" y="18" width="28" height="22" rx="2"/><path d="M10 26h28M17 18v-4M24 18v-4M31 18v-4M20 32h8"/>'),
    off: svgIcon('<path d="M6 22h36M9 22v18M39 22v18M26 22v11h13M29 27h7M14 22v-6h10v6"/>'),
    bal: svgIcon('<path d="M8 28h32M8 28v12M16 28v12M24 28v12M32 28v12M40 28v12M6 40h36M24 22v-5M24 17c-3 0-6-2-6-6 4 0 6 2 6 6zm0 0c3 0 6-2 6-6-4 0-6 2-6 6z"/>'),
    opn: svgIcon('<path d="M6 33v-5a2 2 0 0 1 4 0v1h10v-1a2 2 0 0 1 4 0v5zM8 33v4M22 33v4M31 24h12M32 24v13M42 24v13M28 37h16"/>'),
    bath: svgIcon('<path d="M10 24v-6a4 4 0 0 1 4-4h1M8 24h32a2 2 0 0 1 2 2v2a8 8 0 0 1-8 8H14a8 8 0 0 1-8-8v-2a2 2 0 0 1 2-2zM16 40v2M32 40v2"/>'),
    util: svgIcon('<rect x="10" y="8" width="28" height="32" rx="3"/><circle cx="24" cy="26" r="9"/><circle cx="24" cy="26" r="4"/><circle cx="15" cy="13" r="1"/><circle cx="20" cy="13" r="1"/>'),
    foyer: svgIcon('<path d="M17 40V10h14v30M31 26h-3M10 40h28M14 40v-4M34 40v-4"/>'),
    oth: svgIcon('<path d="M18 8h12l3 12H15zM24 20v18M17 40h14M24 38v2"/>')
  };

  if (typeof document !== 'undefined' && !document.getElementById('sib-quiz-room-css')) {
    var flowStyle = document.createElement('style');
    flowStyle.id = 'sib-quiz-room-css';
    flowStyle.textContent = FLOW_CSS;
    document.head.appendChild(flowStyle);
  }

  SibQuiz.prototype.connectedCallback = function () {
    var q = this;
    q.cfg = JSON.parse(q.querySelector('[data-quiz-config]').textContent);
    q.E = window.SIBEstimator;
    q.answers = {
      existingSiteCondition: '', projectPurpose: '',
      homeType: '', sqft: 1000, projectRooms: [], roomCounts: {}, propertyName: '',
      location: '', services: [], budgetBand: '', contractorPref: '',
      tier: 'p', works: ['paint', 'kit', 'ward']
    };
    q.pii = { name: '', email: '', phone: '', marketingOptIn: false };
    q.submissionId = uuid();
    q.submitting = false; q.submitted = false;
    try {
      var saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      if (saved && saved.answers) { q.answers = Object.assign(q.answers, saved.answers); q.submissionId = saved.submissionId || q.submissionId; }
    } catch (e) {}
    /* sanitise restored answers against current keys */
    if (!q.answers.roomCounts) q.answers.roomCounts = {};
    q.answers.projectRooms = (q.answers.projectRooms || []).filter(function (k) { return q.E.ROOMS[k]; });
    q.answers.projectRooms.forEach(function (k) { if (!q.answers.roomCounts[k]) q.answers.roomCounts[k] = 1; });
    if (KEYS.siteConditions.indexOf(q.answers.existingSiteCondition) < 0) q.answers.existingSiteCondition = '';
    if (KEYS.projectPurposes.indexOf(q.answers.projectPurpose) < 0) q.answers.projectPurpose = '';
    if (KEYS.homeTypes.indexOf(q.answers.homeType) < 0) q.answers.homeType = '';
    if (KEYS.budgetBands.indexOf(q.answers.budgetBand) < 0) q.answers.budgetBand = '';
    if (KEYS.contractorPrefs.indexOf(q.answers.contractorPref) < 0) q.answers.contractorPref = '';
    q.answers.services = (q.answers.services || []).filter(function (s) { return KEYS.services.indexOf(s) >= 0; });
    if (KEYS.tiers.indexOf(q.answers.tier) < 0) q.answers.tier = 'p';
    q.answers.works = (q.answers.works || []).filter(function (w) { return KEYS.works.indexOf(w) >= 0; });
    q.answers.sqft = q.E.clampSqft(q.answers.sqft);

    q.screens = q.buildScreenList();
    q.idx = 0;
    q.root = q.querySelector('[data-quiz-root]');
    q.progressBar = q.querySelector('[data-quiz-progress]');
    q.liveRegion = q.querySelector('[data-quiz-live]');

    var params = new URLSearchParams(location.search);
    var landing = document.querySelector('[data-quiz-landing]');
    var start = function (push) {
      if (landing) landing.style.display = 'none';
      q.style.display = 'block';
      q.render();
      if (push) history.replaceState(null, '', location.pathname + '?start=1');
      q.root.focus({ preventScroll: false });
      window.scrollTo({ top: 0 });
    };
    q.startQuiz = start;
    if (params.get('start') === '1') start(false);
    document.querySelectorAll('[data-quiz-start]').forEach(function (btn) {
      btn.addEventListener('click', function (ev) { ev.preventDefault(); start(true); });
    });
  };

  /* room quantity model: roomCounts is the source of truth; projectRooms mirrors its keys */
  SibQuiz.prototype.setRoomCount = function (key, n) {
    n = Math.max(0, Math.min(9, n));
    if (n === 0) {
      delete this.answers.roomCounts[key];
      this.answers.projectRooms = this.answers.projectRooms.filter(function (k) { return k !== key; });
    } else {
      this.answers.roomCounts[key] = n;
      if (this.answers.projectRooms.indexOf(key) < 0) this.answers.projectRooms.push(key);
    }
    this.persist();
  };

  SibQuiz.prototype.persist = function () {
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ answers: this.answers, submissionId: this.submissionId })); } catch (e) {}
  };

  /* copy helper: section setting, else code fallback */
  SibQuiz.prototype.t = function (key, fallback) {
    var c = this.cfg.copy || {};
    return c[key] != null && c[key] !== '' ? c[key] : fallback;
  };

  /* 'works' only appears once the visitor asks for a contractor estimate. Site condition is
     just New developer unit vs Resale/older — no age follow-up; resale properties use the
     model's conservative "Unknown old" age band throughout (matches 06_Age_Condition).
     'purpose' (own stay vs rental) sits immediately after 'site', per spec. */
  SibQuiz.prototype.buildScreenList = function () {
    var list = ['site', 'purpose', 'home', 'size', 'rooms', 'property', 'location', 'services', 'budget', 'contractor'];
    if (this.answers && this.answers.contractorPref === 'include') list.push('works');
    return list.concat(['name', 'contact']);
  };

  /* id-anchored navigation: the list can grow/shrink at the contractor branch */
  SibQuiz.prototype.go = function (delta) {
    var cur = this.screens[this.idx];
    this.screens = this.buildScreenList();
    var i = this.screens.indexOf(cur);
    if (i < 0) i = Math.min(this.idx, this.screens.length - 1);
    this.idx = Math.max(0, Math.min(this.screens.length - 1, i + delta));
    this.render();
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  SibQuiz.prototype.setProgress = function (pct) {
    if (pct == null) pct = Math.round(((this.idx + 1) / this.screens.length) * 100);
    if (this.progressBar) {
      this.progressBar.style.width = pct + '%';
      this.progressBar.parentElement.setAttribute('aria-valuenow', String(pct));
    }
  };

  SibQuiz.prototype.announce = function (msg) { if (this.liveRegion) this.liveRegion.textContent = msg; };

  /* option list: use the section's list only when its keys match the code contract */
  SibQuiz.prototype.opts = function (name) {
    var cfgList = this.cfg[name];
    var keys = KEYS[name];
    if (Array.isArray(cfgList) && cfgList.length === keys.length &&
        cfgList.every(function (o) { return o && keys.indexOf(o.value) >= 0; })) return cfgList;
    return OPTS[name];
  };

  /* ---------- rendering ---------- */
  SibQuiz.prototype.render = function () {
    var q = this, id = q.screens[q.idx], E = q.E;
    q.setProgress();
    q.root.innerHTML = '';
    var s = el('div', 'sq-screen');
    var validState = { valid: false };
    var head = el('div', 'sq-head');

    var screens = {
      site: function () {
        head.appendChild(el('h2', 'sq-h', q.t('site_heading', 'Is this a new developer unit, or a resale / older property?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('siteConditions'), 'existingSiteCondition', validState, true));
        validState.valid = validators.oneOf(q.answers.existingSiteCondition, KEYS.siteConditions);
        validState.noNext = true;
      },
      purpose: function () {
        head.appendChild(el('h2', 'sq-h', q.t('purpose_heading', 'Is this property for your own stay, or for rental?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('projectPurposes'), 'projectPurpose', validState, true, function (opt) {
          /* Rental pre-selects Interior Styling + Procurement Service (assuming minimal works)
             — only if services haven't been touched yet, so it never clobbers a manual pick
             made earlier in the flow (e.g. after Back-ing up from a later screen). Selections
             stay fully editable on the Services screen either way. */
          if (opt.value === 'rental' && q.answers.services.length === 0) {
            q.answers.services = RENTAL_DEFAULT_SERVICES.slice();
            q.persist();
          }
        }));
        validState.valid = validators.oneOf(q.answers.projectPurpose, KEYS.projectPurposes);
        validState.noNext = true;
      },
      home: function () {
        head.appendChild(el('h2', 'sq-h', q.t('home_heading', 'What type of home is this?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('homeTypes'), 'homeType', validState, true));
        validState.valid = validators.oneOf(q.answers.homeType, KEYS.homeTypes);
        validState.noNext = true;
      },
      size: function () {
        head.appendChild(el('h2', 'sq-h', q.t('size_heading', 'Approximately how large is your home?')));
        s.appendChild(head);
        var wrap = el('div', 'sq-sliderwrap');
        var row = el('div', 'sq-sliderrow');
        row.appendChild(el('span', 'sq-sllabel', q.t('size_label', 'Built-up area')));
        var out = el('span', 'sq-slout', q.answers.sqft.toLocaleString('en-MY') + ' sq ft');
        row.appendChild(out);
        wrap.appendChild(row);
        var slider = el('input');
        slider.type = 'range';
        slider.min = String(E.LIMITS.minSqft); slider.max = String(E.LIMITS.maxSqft);
        slider.step = '50'; slider.value = String(q.answers.sqft);
        slider.setAttribute('aria-label', 'Built-up area in square feet');
        slider.addEventListener('input', function () {
          q.answers.sqft = E.clampSqft(slider.value);
          out.textContent = q.answers.sqft.toLocaleString('en-MY') + ' sq ft';
          q.persist();
        });
        wrap.appendChild(slider);
        s.appendChild(wrap);
        validState.valid = true;
        validState.alwaysNext = true;
      },
      rooms: function () {
        head.appendChild(el('h2', 'sq-h', q.t('rooms_heading', 'Which spaces would you like help with?')));
        head.appendChild(el('p', 'sq-sub', q.t('rooms_sub', 'Select as many as you like.')));
        s.appendChild(head);
        var roomOpts = (q.cfg.rooms || []).filter(function (r) { return E.ROOMS[r.key]; });
        if (!roomOpts.length) roomOpts = Object.keys(E.ROOMS).map(function (k) { return { key: k, label: E.ROOMS[k] }; });
        if (!roomOpts.some(function (r) { return r.key === 'oth'; })) roomOpts.push({ key: 'oth', label: 'Others' });
        var grid = el('div', 'sq-roomgrid');
        grid.setAttribute('role', 'group'); grid.setAttribute('aria-label', q.t('rooms_heading', 'Which spaces would you like help with?'));
        roomOpts.forEach(function (room) {
          var n = q.answers.roomCounts[room.key] || 0;
          var item = el('div', 'sq-roomitem');
          var b = el('button', 'sq-roomcircle');
          b.type = 'button'; b.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
          b.setAttribute('aria-label', room.label + (n > 0 ? ', selected × ' + n : ''));
          var shortLabel = room.label.replace(/\s*\(.*\)\s*/, '');
          b.innerHTML = (ICONS[room.key] || '') +
            '<span class="sq-roomlabelin">' + shortLabel + '</span>' +
            (n > 0 ? '<span class="sq-roomcheck" aria-hidden="true">✓</span>' : '');
          b.addEventListener('click', function () { q.setRoomCount(room.key, n > 0 ? 0 : 1); q.render(); });
          item.appendChild(b);
          if (n > 0) {
            var step = el('div', 'sq-roomstep');
            var minus = el('button', null, '&minus;'); minus.type = 'button';
            minus.setAttribute('aria-label', 'Fewer: ' + room.label);
            minus.addEventListener('click', function () { q.setRoomCount(room.key, n - 1); q.render(); });
            var plus = el('button', null, '+'); plus.type = 'button';
            plus.setAttribute('aria-label', 'More: ' + room.label);
            plus.addEventListener('click', function () { q.setRoomCount(room.key, n + 1); q.render(); });
            step.appendChild(minus);
            step.appendChild(el('span', 'sq-roomcount', String(n)));
            step.appendChild(plus);
            item.appendChild(step);
          }
          grid.appendChild(item);
        });
        s.appendChild(grid);
        validState.valid = validators.nonEmptyArray(q.answers.projectRooms);
        validState.alwaysNext = true;
        var total = q.answers.projectRooms.reduce(function (sum, k) { return sum + (q.answers.roomCounts[k] || 1); }, 0);
        validState.totalLabel = total + (total === 1 ? ' room' : ' rooms');
      },
      property: function () {
        head.appendChild(el('h2', 'sq-h', q.t('q5_heading', 'What is the name of the property?')));
        s.appendChild(head);
        s.appendChild(q.textField('propertyName', q.t('q5_placeholder', 'e.g. The Rainz, Bukit Jalil'), 'text', validState, false));
        validState.valid = true;
      },
      location: function () {
        head.appendChild(el('h2', 'sq-h', q.t('q6_heading', 'Where is your home located?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('locations'), 'location', validState, true));
        var note = q.t('q6_note', 'Our interior design and styling services are currently offered for properties in Klang Valley.');
        if (note) s.appendChild(el('p', 'sq-note', note));
        validState.valid = validators.oneOf(q.answers.location, KEYS.locations);
        validState.noNext = true;
      },
      services: function () {
        head.appendChild(el('h2', 'sq-h', q.t('services_heading', 'How would you like us to help?')));
        head.appendChild(el('p', 'sq-sub', q.t('services_sub', 'Select all that apply.')));
        s.appendChild(head);
        var grid = el('div', 'sq-grid');
        grid.setAttribute('role', 'group'); grid.setAttribute('aria-label', q.t('services_heading', 'How would you like us to help?'));
        q.opts('services').forEach(function (opt) {
          var on = q.answers.services.indexOf(opt.value) >= 0;
          var b = el('button', 'sq-card');
          b.type = 'button'; b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.innerHTML = '<span class="sq-cardlabel">' + opt.label + '</span>' + (opt.desc ? '<span class="sq-carddesc">' + opt.desc + '</span>' : '');
          b.addEventListener('click', function () {
            var svcs = q.answers.services;
            var i = svcs.indexOf(opt.value);
            if (i >= 0) { svcs.splice(i, 1); }
            else if (opt.value === EXCLUSIVE_SERVICE) { q.answers.services = [EXCLUSIVE_SERVICE]; }
            else {
              var j = svcs.indexOf(EXCLUSIVE_SERVICE);
              if (j >= 0) svcs.splice(j, 1);
              svcs.push(opt.value);
            }
            q.persist(); q.render();
          });
          grid.appendChild(b);
        });
        s.appendChild(grid);
        if (q.answers.projectPurpose === 'rental') {
          var rentalNote = q.t('purpose_rental_note', 'For rental units, we’ve preselected Interior Styling and Procurement Service as we’re assuming minimal works are required. If you’d like a full interior design, you can select Interior Design instead of Interior Styling.');
          if (rentalNote) s.appendChild(el('p', 'sq-note', rentalNote));
        }
        validState.valid = validators.nonEmptyArray(q.answers.services);
        validState.alwaysNext = true;
      },
      budget: function () {
        head.appendChild(el('h2', 'sq-h', q.t('budget_heading', 'What are you planning to invest in your home?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('budgetBands'), 'budgetBand', validState, true));
        validState.valid = validators.oneOf(q.answers.budgetBand, KEYS.budgetBands);
        validState.noNext = true;
      },
      contractor: function () {
        head.appendChild(el('h2', 'sq-h', q.t('contractor_heading', 'Would you like us to include an estimate for contractor works?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('contractorPrefs'), 'contractorPref', validState, true));
        validState.valid = validators.oneOf(q.answers.contractorPref, KEYS.contractorPrefs);
        validState.noNext = true;
      },
      works: function () {
        head.appendChild(el('h2', 'sq-h', q.t('works_heading', 'Contractor works')));
        head.appendChild(el('p', 'sq-sub sq-worksintro', q.t('works_intro', 'We design and project-manage — the building work is carried out by trusted KL contractors. We gather and compare their quotes, and help you spend your budget where it matters most. Use this range to plan.')));
        s.appendChild(head);
        s.appendChild(el('p', 'sq-lbl', q.t('works_finish_label', 'Finish level — this is where materials and brands live')));
        var tg = el('div', 'sq-tiergrid');
        q.opts('tiers').forEach(function (t) {
          var b = el('button', 'sq-tier' + (q.answers.tier === t.value ? ' on' : ''));
          b.type = 'button'; b.setAttribute('aria-pressed', q.answers.tier === t.value ? 'true' : 'false');
          b.innerHTML = '<b>' + t.label + '</b><span>' + t.desc + '</span>';
          b.addEventListener('click', function () { q.answers.tier = t.value; q.persist(); q.render(); });
          tg.appendChild(b);
        });
        s.appendChild(tg);
        s.appendChild(el('p', 'sq-lbl', q.t('works_include_label', 'What the works include')));
        var wg = el('div', 'sq-scpgrid');
        q.opts('works').forEach(function (w) {
          var lab = el('label', 'sq-scp');
          var cb = el('input'); cb.type = 'checkbox';
          cb.checked = q.answers.works.indexOf(w.value) >= 0;
          cb.addEventListener('change', function () {
            var i = q.answers.works.indexOf(w.value);
            if (cb.checked && i < 0) q.answers.works.push(w.value);
            if (!cb.checked && i >= 0) q.answers.works.splice(i, 1);
            q.persist();
          });
          lab.appendChild(cb);
          lab.appendChild(el('span', null, w.label));
          wg.appendChild(lab);
        });
        s.appendChild(wg);
        validState.valid = true;
        validState.alwaysNext = true;
      },
      name: function () {
        head.appendChild(el('h2', 'sq-h', q.t('q8_heading', 'What should we call you?')));
        s.appendChild(head);
        s.appendChild(q.textField('__name', q.t('q8_placeholder', 'Your name'), 'text', validState, true));
        validState.valid = validators.required(q.pii.name);
      },
      contact: function () {
        var first = (q.pii.name.trim().split(/\s+/)[0]) || 'there';
        var msg = q.t('contact_heading', 'Hi [name], let’s make your home feel like you.')
          .replace('[name]', first).replace('{{ name }}', first);
        head.appendChild(el('h2', 'sq-h', msg));
        head.appendChild(el('p', 'sq-sub', q.t('contact_sub1', 'Your personalised estimate is ready.')));
        head.appendChild(el('p', 'sq-sub', q.t('contact_sub2', 'Enter your email to see it now and receive a copy.')));
        s.appendChild(head);
        s.appendChild(q.textField('__email', q.t('q9_placeholder', 'you@email.com'), 'email', validState, true));
        s.appendChild(q.textField('__phone', q.t('q9_phone_placeholder', 'Mobile / WhatsApp (e.g. +60 12 345 6789)'), 'tel', validState, true));
        var consentWrap = el('label', 'sq-consent');
        var cb = el('input'); cb.type = 'checkbox'; cb.checked = q.pii.marketingOptIn;
        cb.addEventListener('change', function () { q.pii.marketingOptIn = cb.checked; });
        consentWrap.appendChild(cb);
        consentWrap.appendChild(el('span', null, q.t('q9_consent', 'Also send me occasional home inspiration and studio news.')));
        s.appendChild(consentWrap);
        s.appendChild(el('p', 'sq-privacy', q.t('q9_privacy', 'We use your details only to prepare and send your estimate. No spam, ever.')));
        s.appendChild(el('div', 'sq-status', ''));
        validState.valid = validators.email(q.pii.email) && validators.phone(q.pii.phone);
        validState.nextLabel = q.t('contact_submit', 'See my estimate →');
        validState.isSubmit = true;
        validState.noBack = true;   /* per spec: no Back on the contact screen */
        validState.center = true;   /* CTA centred */
      }
    };
    screens[id]();
    if (id === 'rooms') s.className = 'sq-screen sq-split';

    /* nav: Back left; right cluster = [total] [Next] — or a single centred CTA */
    var nav = el('div', 'sq-nav' + (validState.center ? ' sq-navcenter' : ''));
    if (q.idx > 0 && !validState.noBack) {
      var back = el('button', 'sq-back', q.t('back_label', 'Back'));
      back.type = 'button';
      back.addEventListener('click', function () { q.go(-1); });
      nav.appendChild(back);
    }
    var right = [];
    if (validState.totalLabel) right.push(el('span', 'sq-total', validState.totalLabel));
    if (!validState.noNext && (validState.valid || validState.alwaysNext)) {
      var next = el('button', 'sq-next', validState.nextLabel || q.t('next_label', 'Next'));
      next.type = 'button';
      if (!validState.valid) next.disabled = true;
      else if (validState.isSubmit) next.addEventListener('click', function () { q.submit(next); });
      else next.addEventListener('click', function () { q.go(1); });
      right.push(next);
    }
    right.forEach(function (elm, i) {
      if (!validState.center) elm.style.marginLeft = i === 0 ? 'auto' : '18px';
      nav.appendChild(elm);
    });
    s.appendChild(nav);
    q.root.appendChild(s);
  };

  SibQuiz.prototype.choiceGrid = function (options, field, validState, single, onSelect) {
    var q = this;
    var grid = el('div', 'sq-grid');
    grid.setAttribute('role', 'radiogroup');
    options.forEach(function (opt) {
      var on = q.answers[field] === opt.value;
      var b = el('button', 'sq-card');
      b.type = 'button'; b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.innerHTML = '<span class="sq-cardlabel">' + opt.label + '</span>' + (opt.desc ? '<span class="sq-carddesc">' + opt.desc + '</span>' : '');
      b.addEventListener('click', function () {
        if (q._advancing) return;
        q.answers[field] = opt.value;
        if (onSelect) onSelect(opt);
        q.persist();
        if (single) {
          q._advancing = true;
          Array.prototype.forEach.call(grid.children, function (x) { x.setAttribute('aria-checked', 'false'); });
          b.setAttribute('aria-checked', 'true');
          setTimeout(function () { q._advancing = false; q.go(1); }, 180);
        } else {
          q.render();
        }
      });
      grid.appendChild(b);
    });
    return grid;
  };

  SibQuiz.prototype.textField = function (field, placeholder, type, validState, required) {
    var q = this;
    var isPII = field === '__name' || field === '__email' || field === '__phone';
    var wrap = el('div', 'sq-fieldwrap');
    var input = el('input', 'sq-field');
    input.type = type; input.placeholder = placeholder || '';
    input.required = !!required;
    input.value = isPII ? (field === '__name' ? q.pii.name : (field === '__phone' ? q.pii.phone : q.pii.email)) : (q.answers[field] || '');
    input.setAttribute('aria-label', placeholder || field);
    var isFinal = field === '__email' || field === '__phone'; /* both live on the contact screen */
    input.addEventListener('input', function () {
      if (field === '__name') q.pii.name = input.value;
      else if (field === '__email') q.pii.email = input.value;
      else if (field === '__phone') q.pii.phone = input.value;
      else { q.answers[field] = input.value; q.persist(); }
      var ok = isFinal ? (validators.email(q.pii.email) && validators.phone(q.pii.phone))
        : (!required || validators.required(input.value));
      var nav = q.root.querySelector('.sq-nav');
      var existing = nav.querySelector('.sq-next');
      if (ok && !existing) {
        var next = el('button', 'sq-next', (isFinal ? q.t('contact_submit', 'See my estimate →') : q.t('next_label', 'Next')));
        next.type = 'button';
        if (isFinal) next.addEventListener('click', function () { q.submit(next); });
        else next.addEventListener('click', function () { q.go(1); });
        if (!nav.classList.contains('sq-navcenter')) next.style.marginLeft = 'auto';
        nav.appendChild(next);
      } else if (!ok && existing) existing.remove();
    });
    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') { ev.preventDefault(); var n = q.root.querySelector('.sq-next'); if (n) n.click(); }
    });
    wrap.appendChild(input);
    if (field !== '__phone') setTimeout(function () { input.focus(); }, 60);
    return wrap;
  };

  /* ---------- estimate state + submit + report ---------- */
  /* engine service key: interior design / virtual design price at the design rate; styling-only at the styling rate */
  SibQuiz.prototype.svcKey = function () {
    var s = this.answers.services;
    if (s.indexOf('design') >= 0 || s.indexOf('virtual') >= 0) return 'd';
    if (s.indexOf('styling') >= 0) return 's';
    return 'd';
  };
  SibQuiz.prototype.worksIncluded = function () { return this.answers.contractorPref === 'include'; };

  /* UI tier letter -> customer-facing / BOQ-model tier name. v7 uses Essential/Premium/
     Signature as BOTH the customer-facing label and the BOQ engine's own tier vocabulary —
     one map, so there is no internal-vs-display split left to drift out of sync (the v2/v6
     engine's separate 'Budget'/'Mid'/'High' internal codenames leaked into the on-page report
     and both emails as literal "Budget" text; see git history). */
  var TIER_LABEL = { e: 'Essential', p: 'Premium', s: 'Signature' };
  var WORKS_TO_BOQ_FLAGS = function (works) {
    var has = function (k) { return works.indexOf(k) >= 0; };
    return {
      plumbing: has('plumb'), flooring: has('floor'), paintingCeiling: has('paint'),
      electrical: has('elec'), kitchenCarpentry: has('kit'), builtInWardrobe: has('ward'), ffe: has('ffe'),
      wetTiling: has('wet')
    };
  };

  /* Real construction-cost estimate from the Klang Valley BOQ model (sib-boq-engine.js) —
     this is the "contractor works" range; the Sundays in Bed design fee is priced separately
     by sib-estimator.js and is not part of this workbook. */
  SibQuiz.prototype.constructionEstimate = function () {
    var q = this, a = q.answers, boq = window.SIBBoqEngine;
    if (!boq) return null;
    var roomCounts = {};
    Object.keys(a.roomCounts).forEach(function (k) {
      var boqKey = k === 'oth' ? 'others' : k;
      roomCounts[boqKey] = a.roomCounts[k];
    });
    return boq.estimateProject({
      existingSiteCondition: a.existingSiteCondition || 'new_developer',
      propertyAgeYears: 0, /* resale with no age given -> conservative "Unknown old" band (06_Age_Condition) */
      propertyPhysicalCondition: 'Average', /* not asked — kept light, per spec's documented default */
      propertyType: HOME_TYPE_TO_PROPERTY_TYPE[a.homeType] || 'Condominium',
      finishTier: TIER_LABEL[a.tier] || 'Premium',
      roomCounts: roomCounts,
      works: WORKS_TO_BOQ_FLAGS(a.works),
      layoutChange: 'Keep layout',
      projectPurpose: a.projectPurpose || 'own_stay',
      sqft: a.sqft /* v9: drives the whole-home size-zone engine (MODEL_CONFIG 3D) — without
                      this every submission would silently price as the smallest zone */
    });
  };

  SibQuiz.prototype.estimateState = function () {
    var q = this, E = q.E;
    var counts = {};
    Object.keys(E.ROOMS).forEach(function (k) { counts[k] = 0; });
    q.answers.projectRooms.forEach(function (k) { if (counts[k] != null) counts[k] = q.answers.roomCounts[k] || 1; });
    return {
      counts: counts,
      sqft: q.answers.sqft,
      svc: q.svcKey(),
      tier: q.worksIncluded() ? q.answers.tier : q.E.DEFAULTS.tier,
      works: q.worksIncluded() ? q.answers.works.slice() : []
    };
  };
  SibQuiz.prototype.calculatorURL = function () {
    var st = this.estimateState();
    var rooms = Object.keys(st.counts).filter(function (k) { return st.counts[k] > 0; })
      .map(function (k) { return k + ':' + st.counts[k]; }).join(',');
    return (this.cfg.calculatorPath || '/pages/cost-calculator') + '?quiz=1&rooms=' + encodeURIComponent(rooms) +
      '&sqft=' + st.sqft + '&svc=' + st.svc + '&tier=' + st.tier + '&works=' + encodeURIComponent(st.works.join(','));
  };

  /* Plain-text lead summary for the studio (Shopify contact-form fallback). */
  SibQuiz.prototype.leadSummary = function () {
    var q = this, E = q.E, a = q.answers;
    var st = q.estimateState();
    var r = E.estimate(st);
    var boq = q.worksIncluded() ? q.constructionEstimate() : null;
    var included = !!(boq && boq.directBoq.expected > 0);
    var rooms = Object.keys(st.counts).filter(function (k) { return st.counts[k] > 0; })
      .map(function (k) { return E.ROOMS[k] + (st.counts[k] > 1 ? ' x' + st.counts[k] : ''); }).join(', ');
    var services = a.services.map(function (v) { return optLabel(q.opts('services'), v); }).join(' + ');
    var lines = [
      'New estimate enquiry from ' + q.pii.name.trim(),
      '',
      'Email: ' + q.pii.email.trim(),
      'WhatsApp: ' + q.pii.phone.trim(),
      'Newsletter opt-in: ' + (q.pii.marketingOptIn ? 'yes' : 'no'),
      '',
      'Property: ' + (a.propertyName || '—'),
      'Site condition: ' + optLabel(q.opts('siteConditions'), a.existingSiteCondition),
      'Own stay or rental: ' + optLabel(q.opts('projectPurposes'), a.projectPurpose),
      'Location: ' + optLabel(q.opts('locations'), a.location),
      'Home type: ' + optLabel(q.opts('homeTypes'), a.homeType),
      'Size: ' + a.sqft.toLocaleString('en-MY') + ' sq ft',
      'Spaces: ' + rooms,
      'Services: ' + (services || '—'),
      'Planned investment: ' + optLabel(q.opts('budgetBands'), a.budgetBand),
      'Contractor works: ' + optLabel(q.opts('contractorPrefs'), a.contractorPref),
      '',
      'Design fee (fixed): ' + E.fmtRM(r.fee)
    ];
    if (included) {
      var tierName = TIER_LABEL[a.tier] || 'Premium';
      lines.push('Finish level: ' + tierName);
      lines.push('Works: ' + a.works.map(function (w) { return optLabel(OPTS.works, w); }).join(', '));
      lines.push('Contractor works, BOQ model (estimated): ' + E.fmtRM(E.r100(boq.total.low)) + ' - ' + E.fmtRM(E.r100(boq.total.high)));
      lines.push('Age band used: ' + boq.ageBand + ' | recommended contingency (reference only, not in total): ' + Math.round(boq.recommendedContingency * 1000) / 10 + '%');
      lines.push('Plan around: ' + E.fmtRM(E.r100(r.fee + boq.total.low)) + ' - ' + E.fmtRM(E.r100(r.fee + boq.total.high)));
    } else {
      lines.push('Contractor works: not included in this estimate');
      lines.push('Plan around: ' + E.fmtRM(r.fee));
    }
    lines.push('', 'Adjustable version: ' + location.origin + q.calculatorURL());
    return lines.join('\n');
  };

  /* Fallback delivery: Shopify's native contact form — reaches the studio inbox with no third-party keys. */
  SibQuiz.prototype.sendViaShopifyContact = function () {
    var q = this;
    var body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    body.set('contact[name]', q.pii.name.trim() + ' (estimate flow)');
    body.set('contact[email]', q.pii.email.trim());
    body.set('contact[phone]', q.pii.phone.trim());
    body.set('contact[body]', q.leadSummary());
    return fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    }).then(function (res) { return { ok: res.ok }; });
  };

  /* The post-submission report: same visual structure as /pages/cost-calculator.
     The customer's EMAIL is already sent server-side with the submitted answers (frozen) before
     this renders — so editing here never changes what was emailed. On the website, when a
     contractor estimate was requested, the Finish level and Works controls stay EDITABLE so the
     contractor price can be adjusted live; the figures recompute in place. Everything else
     (property, rooms, service, etc.) is read-only.
     Design fee = sib-estimator.js (per-room service fee); contractor works = the Klang Valley
     BOQ model (sib-boq-engine.js), the sole costing authority for that half. Finish tier drives
     both — as the live calculator already behaves. */
  SibQuiz.prototype.showReport = function (statusMsg) {
    var q = this, E = q.E, a = q.answers;
    var esc = function (t) { return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
    var included = q.worksIncluded();
    var disc = q.t('report_disclaimer', 'Estimates are indicative — every range is confirmed with real quotes before anything is agreed — and exclude structural or wet works. We’re a design consultation studio, not a design-and-build firm: works are quoted and carried out by reputable KL contractors, and we gather and compare those quotes for you. Klang Valley site visits RM200, deducted if you proceed.');

    var st = q.estimateState();
    var rooms = Object.keys(st.counts).filter(function (k) { return st.counts[k] > 0; })
      .map(function (k) { return E.ROOMS[k].replace(/\s*\(.*\)\s*/, '') + (st.counts[k] > 1 ? ' ×' + st.counts[k] : ''); }).join(', ');
    var services = a.services.map(function (v) { return optLabel(q.opts('services'), v); }).join(' + ');

    var sumRows = [
      ['Property', a.propertyName.trim() || '—'],
      ['Site condition', optLabel(q.opts('siteConditions'), a.existingSiteCondition)],
      ['Own stay or rental', optLabel(q.opts('projectPurposes'), a.projectPurpose)],
      ['Location', optLabel(q.opts('locations'), a.location)],
      ['Home type', optLabel(q.opts('homeTypes'), a.homeType)],
      ['Built-up area', a.sqft.toLocaleString('en-MY') + ' sq ft'],
      ['Spaces', rooms || '—'],
      ['Service', services || '—'],
      ['Planned investment', optLabel(q.opts('budgetBands'), a.budgetBand)]
    ];
    if (!included) sumRows.push(['Contractor works', optLabel(q.opts('contractorPrefs'), a.contractorPref)]);

    var wrapEl = el('div', 'sq-report');
    if (statusMsg) wrapEl.appendChild(el('p', 'sq-repstatus', esc(statusMsg)));

    var sum = el('div', 'sq-repsum');
    sum.appendChild(el('h3', null, esc(q.t('report_heading', 'Your estimate'))));
    sumRows.forEach(function (row) {
      var r = el('div', 'sq-reprow');
      r.appendChild(el('span', 'sq-replbl', esc(row[0])));
      r.appendChild(el('span', 'sq-repval', esc(row[1])));
      sum.appendChild(r);
    });
    wrapEl.appendChild(sum);

    /* editable finish-level + works block (only when a contractor estimate was requested) */
    if (included) {
      var edit = el('div', 'sq-repedit');
      edit.appendChild(el('p', 'sq-editnote', q.t('report_edit_note', 'Adjust the finish level or works below to explore how the contractor estimate changes. Your emailed copy keeps the answers you submitted.')));
      edit.appendChild(el('p', 'sq-lbl', q.t('works_finish_label', 'Finish level — this is where materials and brands live')));
      var tg = el('div', 'sq-tiergrid');
      q.opts('tiers').forEach(function (t) {
        var b = el('button', 'sq-tier' + (a.tier === t.value ? ' on' : ''));
        b.type = 'button'; b.setAttribute('aria-pressed', a.tier === t.value ? 'true' : 'false');
        b.innerHTML = '<b>' + t.label + '</b><span>' + t.desc + '</span>';
        b.addEventListener('click', function () { a.tier = t.value; refresh(); });
        tg.appendChild(b);
      });
      edit.appendChild(tg);
      edit.appendChild(el('p', 'sq-lbl', q.t('works_include_label', 'What the works include')));
      var wg = el('div', 'sq-scpgrid');
      q.opts('works').forEach(function (w) {
        var lab = el('label', 'sq-scp');
        var cb = el('input'); cb.type = 'checkbox';
        cb.checked = a.works.indexOf(w.value) >= 0;
        cb.addEventListener('change', function () {
          var i = a.works.indexOf(w.value);
          if (cb.checked && i < 0) a.works.push(w.value);
          if (!cb.checked && i >= 0) a.works.splice(i, 1);
          refresh();
        });
        lab.appendChild(cb);
        lab.appendChild(el('span', null, w.label));
        wg.appendChild(lab);
      });
      edit.appendChild(wg);
      wrapEl.appendChild(edit);
    }

    /* answer panel — figures get stable classes so refresh() can update them in place */
    var ans = el('div', 'sq-repans');
    var sep = el('div', 'sq-anssep');
    var feeRow = el('div', 'sq-ansrow');
    feeRow.appendChild(el('span', 'sq-anslbl', 'Design fee — fixed'));
    feeRow.appendChild(el('span', 'sq-ansval sq-rep-fee', ''));
    sep.appendChild(feeRow);
    var worksRow = el('div', 'sq-ansrow');
    worksRow.appendChild(el('span', 'sq-anslbl', 'Contractor works — estimated'));
    worksRow.appendChild(el('span', 'sq-ansval sq-rep-works', ''));
    sep.appendChild(worksRow);
    ans.appendChild(sep);
    var planRow = el('div', 'sq-ansrow');
    planRow.appendChild(el('span', 'sq-anslbl', 'Plan around'));
    planRow.appendChild(el('span', 'sq-anstotal sq-rep-plan', ''));
    ans.appendChild(planRow);
    ans.appendChild(el('p', 'sq-ansnote sq-rep-note', ''));
    var ctaWrap = el('div', null, ''); ctaWrap.style.textAlign = 'center';
    var cta = el('a', 'sq-anscta', esc(q.t('report_cta_label', 'Schedule a discovery call →')));
    cta.href = q.t('report_cta_link', '/pages/contact');
    ctaWrap.appendChild(cta);
    ans.appendChild(ctaWrap);
    ans.appendChild(el('p', 'sq-ansdisc', esc(disc)));
    wrapEl.appendChild(ans);

    /* recompute figures from current answers and paint them into the panel */
    function refresh() {
      var st2 = q.estimateState();
      var r2 = E.estimate(st2);
      var boq = included ? q.constructionEstimate() : null;
      var hasWorks = !!(boq && boq.directBoq.expected > 0);
      var tierName = TIER_LABEL[a.tier] || 'Premium';
      var worksVal = hasWorks ? (E.fmtRM(E.r100(boq.total.low)) + ' – ' + E.fmtRM(E.r100(boq.total.high))) : q.t('report_works_excluded', 'Not included in this estimate');
      var planVal = hasWorks ? (E.fmtRM(E.r100(r2.fee + boq.total.low)) + ' – ' + E.fmtRM(E.r100(r2.fee + boq.total.high))) : E.fmtRM(r2.fee);
      var note = hasWorks
        ? ('Most ' + tierName + ' projects of this size and scope land inside this band. Tell us your budget and we design around it — gathering contractor quotes and helping you spend where it matters most.')
        : q.t('report_note_noworks', 'Your total here is the fixed design fee — contractor works aren’t part of this estimate.');
      q.root.querySelector('.sq-rep-fee').textContent = E.fmtRM(r2.fee);
      var wEl = q.root.querySelector('.sq-rep-works');
      wEl.textContent = worksVal; wEl.style.fontSize = hasWorks ? '' : '17px';
      q.root.querySelector('.sq-rep-plan').textContent = planVal;
      q.root.querySelector('.sq-rep-note').textContent = note;
      /* keep tier-card selected state in sync when re-clicked */
      Array.prototype.forEach.call(q.root.querySelectorAll('.sq-tier'), function (b) {
        var on = b.querySelector('b') && q.opts('tiers').filter(function (t) { return t.label === b.querySelector('b').textContent; })[0];
        var isOn = on && on.value === a.tier;
        b.classList.toggle('on', !!isOn); b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      });
      q.persist();
    }

    q.setProgress(100);
    q.root.innerHTML = '';
    q.root.appendChild(wrapEl);
    refresh();
    q.announce(q.t('report_heading', 'Your estimate'));
    window.scrollTo({ top: 0 });
  };

  SibQuiz.prototype.submit = function (btn) {
    var q = this;
    if (q.submitting || q.submitted) return;
    var status = q.root.querySelector('.sq-status');
    var finish = function (msg) {
      q.submitted = true;
      try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
      q.showReport(msg);           /* answers stay in memory for the report render */
      q.pii = { name: '', email: '', phone: '', marketingOptIn: false };
    };
    var fail = function () {
      q.submitting = false;
      btn.disabled = false; btn.textContent = q.t('contact_submit', 'See my estimate →');
      status.className = 'sq-status sq-error';
      status.textContent = q.t('error_label', 'We couldn’t send your estimate just now. Please try again.');
      q.announce(status.textContent);
    };
    q.submitting = true;
    btn.disabled = true; btn.textContent = q.t('loading_label', 'Preparing your estimate…');
    status.className = 'sq-status'; status.textContent = '';

    /* Only a real http(s) endpoint counts — anything else must not break the flow. */
    var endpoint = /^https?:\/\//i.test(q.cfg.endpoint || '') ? q.cfg.endpoint : '';
    if (!endpoint) {
      /* No worker yet: deliver the lead to the studio via Shopify, then show the report. */
      q.sendViaShopifyContact().then(function (r) {
        if (!r.ok) throw new Error('contact form failed');
        finish(q.t('success_pending', 'Your details are with our studio — we’ll follow up with your emailed copy shortly.'));
      }).catch(fail);
      return;
    }
    var st = q.estimateState();
    var payload = {
      existingSiteCondition: q.answers.existingSiteCondition,
      projectPurpose: q.answers.projectPurpose,
      homeType: q.answers.homeType,
      sqft: q.answers.sqft,
      projectRooms: q.answers.projectRooms,
      roomCounts: q.answers.roomCounts,
      propertyName: q.answers.propertyName,
      location: q.answers.location,
      services: q.answers.services,
      budgetBand: q.answers.budgetBand,
      contractorPref: q.answers.contractorPref,
      tier: q.answers.tier,
      works: q.answers.works,
      name: q.pii.name.trim(),
      email: q.pii.email.trim(),
      phone: q.pii.phone.trim(),
      marketingOptIn: q.pii.marketingOptIn,
      calculatorState: st,
      calculatorUrl: q.calculatorURL(),
      emailCopy: q.cfg.emailCopy || {},
      submissionId: q.submissionId
    };
    fetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) { return res.json().catch(function () { return {}; }).then(function (j) { return { ok: res.ok && j.ok }; }); })
      .then(function (r) {
        if (r.ok) finish(q.t('success_label', 'A copy is on its way to your inbox.'));
        else throw new Error('send failed');
      })
      .catch(fail);
  };

  if (!customElements.get('sib-quiz')) customElements.define('sib-quiz', SibQuiz);
  return { validators: validators, KEYS: KEYS, OPTS: OPTS };
}));

